import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useEffect, useRef, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Select from "react-select";
import { useDebounce } from "use-debounce";
import CustomEditor5 from "../../../utils/ckEditor5";
import {
  getJobDetailRepo,
  putJobOpeningRepo,
} from "../../../../store/repositories/jobOpenings";
import {
  getCompanyFilter,
  getDesignationFilter,
  getJobOpenings,
  getJobProfileFilter,
  getJobTypeFilter,
  getLocationFilter,
} from "../../../../store/slices/jobOpenings";
import InfiniteScrollSelect from "../common/InfiniteScrollSelect";
import "./jobOpeningStyle.css";

const MIN_ALLOWED_DATE = "2023-01-01";

const getQueryParam = (url, key) => {
  try {
    const params = new URL(url).searchParams;
    return params.get(key);
  } catch (e) {
    return null;
  }
};

const mergeUniqueByValue = (prev, next) => {
  const map = new Map((prev || []).map((item) => [item.value, item]));
  (next || []).forEach((item) => {
    if (!map.has(item.value)) {
      map.set(item.value, item);
    }
  });
  return Array.from(map.values());
};

const isSameQuery = (a, b) => {
  const left = (a ?? "").toString().trim().toLowerCase();
  const right = (b ?? "").toString().trim().toLowerCase();
  return left === right;
};

const shouldMatchQuery = (query) => (query ?? "").toString().trim().length > 0;

const SELECT_MENU_MAX_HEIGHT = 140;

export const EditJobOpening = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [searchParams] = useSearchParams();
  const jobId = searchParams.get("jobId");

  //the user input data
  const [company, setCompany] = useState("");
  const [jobProfile, setJobProfile] = useState("");
  const [designation, setDesignation] = useState("");
  const [location, setLocation] = useState("");
  const [experience, setExperience] = useState(null);
  const [postDate, setPostDate] = useState("");
  const [applicationEndDate, setApplicationEndDate] = useState("");
  const [url, setUrl] = useState("");
  const [description, setDescription] = useState("");

  //the user selected data
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedJobProfile, setSelectedJobProfile] = useState(null);
  const [selectedDesignation, setSelectedDesignation] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedJobType, setSelectedJobType] = useState(null);

  const [errorAlertOpen, setErrorAlertOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isPublished, setIsPublished] = useState(true); // Status: true = Active, false = Inactive

  const [debouncedCompany] = useDebounce(company, 500);
  const [debouncedJobProfile] = useDebounce(jobProfile, 500);
  const [debouncedDesignation] = useDebounce(designation, 500);
  const [debouncedLocation] = useDebounce(location, 500);

  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyNextCursor, setCompanyNextCursor] = useState(null);
  const [companyHasMore, setCompanyHasMore] = useState(false);
  const companyAppendRef = useRef(false);
  const companyQueryRef = useRef("");

  const [jobProfileOptions, setJobProfileOptions] = useState([]);
  const [jobProfileNextCursor, setJobProfileNextCursor] = useState(null);
  const [jobProfileHasMore, setJobProfileHasMore] = useState(false);
  const jobProfileAppendRef = useRef(false);
  const jobProfileQueryRef = useRef("");

  const [designationOptions, setDesignationOptions] = useState([]);
  const [designationNextCursor, setDesignationNextCursor] = useState(null);
  const [designationHasMore, setDesignationHasMore] = useState(false);
  const designationAppendRef = useRef(false);
  const designationQueryRef = useRef("");

  const [locationOptions, setLocationOptions] = useState([]);
  const [locationNextCursor, setLocationNextCursor] = useState(null);
  const [locationHasMore, setLocationHasMore] = useState(false);
  const locationAppendRef = useRef(false);
  const locationQueryRef = useRef("");

  //displayJobProfile
  const [displayJobProfile, setDisplayJobProfile] = useState(null);

  useEffect(() => {
    if (!debouncedCompany?.trim()) return;
    companyAppendRef.current = false;
    companyQueryRef.current = debouncedCompany;
    dispatch(getCompanyFilter({ field: "company", query: company }));
  }, [debouncedCompany]);

  useEffect(() => {
    if (!debouncedJobProfile?.trim()) return;
    jobProfileAppendRef.current = false;
    jobProfileQueryRef.current = debouncedJobProfile;
    dispatch(
      getJobProfileFilter({
        field: "job_profile",
        query: jobProfile,
      }),
    );
  }, [debouncedJobProfile]);

  useEffect(() => {
    if (!debouncedDesignation?.trim()) return;
    designationAppendRef.current = false;
    designationQueryRef.current = debouncedDesignation;
    dispatch(
      getDesignationFilter({ field: "designation", query: designation }),
    );
  }, [debouncedDesignation]);

  useEffect(() => {
    if (!debouncedLocation?.trim()) return;
    locationAppendRef.current = false;
    locationQueryRef.current = debouncedLocation;
    dispatch(getLocationFilter({ field: "location", query: location }));
  }, [debouncedLocation]);

  //validation state
  const initialError = {
    companyError: false,
    jobProfileError: false,
    postDateError: false,
    appEndDateError: false,
    urlError: false,
    descriptionError: false,
    designationError: false,
    locationError: false,
    jobTypeError: false,
  };
  const [error, setError] = useState(initialError);
  const mendatoryErrorText = "Please fill this field";

  //get states from the redux
  const {
    companyFilterLoading,
    companyFilterData,
    companyFilterError,

    jobProfileFilterLoading,
    jobProfileFilterData,
    jobProfileFilterError,

    locationFilterLoading,
    locationFilterData,
    locationFilterError,

    designationFilterLoading,
    designationFilterData,
    designationFilterError,

    jobTypeFilterLoading,
    jobTypeFilterData,
    jobTypeFilterError,

    message,
  } = useSelector((state) => {
    return state?.jobOpeningsReducer;
  });

  useEffect(() => {
    if (!jobId) {
      navigate("/cms/manage-job-opening");
      return;
    }

    const jobIdExist = async () => {
      try {
        const response = await getJobDetailRepo(jobId);
        if (response?.data?.data) {
          const {
            company,
            company_name,
            job_profile,
            job_profile_name,
            designation,
            designation_name,
            location,
            location_name,
            job_type,
            job_type_label,
            experience_required,
            post_date,
            end_date,
            application_link,
            status,
            description,
          } = response.data.data;
          setSelectedCompany({ value: company, label: company_name });
          setDisplayJobProfile(job_profile_name);
          setSelectedJobProfile({
            value: job_profile,
            label: job_profile_name,
          });
          if (designation && designation_name) {
            setSelectedDesignation({
              value: designation,
              label: designation_name,
            });
          }
          if (location && location_name) {
            setSelectedLocation({ value: location, label: location_name });
          }
          if (job_type && job_type_label) {
            setSelectedJobType({ value: job_type, label: job_type_label });
          }
          if (experience_required) {
            setExperience(experience_required);
          }
          setPostDate(post_date);
          setApplicationEndDate(end_date);
          setUrl(application_link);
          setDescription(description);
          setIsPublished(status === 1 || status === "Active"); // Set status from API
        }
      } catch (e) {
        navigate("/cms/manage-job-opening");
      }
    };

    jobIdExist();
  }, [jobId]);

  useEffect(() => {
    companyQueryRef.current = company;
    jobProfileQueryRef.current = jobProfile;
    designationQueryRef.current = designation;
    locationQueryRef.current = location;
    dispatch(getCompanyFilter({ field: "company", query: company }));
    dispatch(
      getJobProfileFilter({
        field: "job_profile",
        query: jobProfile,
      }),
    );
    dispatch(
      getDesignationFilter({ field: "designation", query: designation }),
    );
    dispatch(getLocationFilter({ field: "location", query: location }));
    dispatch(getJobTypeFilter({ field: "job_type" }));
  }, []);

  useEffect(() => {
    const responseQuery = companyFilterData?.data?.query;
    if (
      responseQuery !== undefined &&
      shouldMatchQuery(responseQuery) &&
      !isSameQuery(responseQuery, companyQueryRef.current)
    ) {
      return;
    }

    const results = companyFilterData?.data?.results || [];
    const nextUrl = companyFilterData?.data?.next;
    const shouldAppend =
      companyAppendRef.current || !!companyFilterData?.data?.previous;
    setCompanyOptions((prev) =>
      shouldAppend ? mergeUniqueByValue(prev, results) : results,
    );
    setCompanyNextCursor(getQueryParam(nextUrl, "cursor"));
    setCompanyHasMore(!!nextUrl);
    companyAppendRef.current = false;
  }, [companyFilterData]);

  useEffect(() => {
    const responseQuery = jobProfileFilterData?.data?.query;
    if (
      responseQuery !== undefined &&
      shouldMatchQuery(responseQuery) &&
      !isSameQuery(responseQuery, jobProfileQueryRef.current)
    ) {
      return;
    }

    const results = jobProfileFilterData?.data?.results || [];
    const nextUrl = jobProfileFilterData?.data?.next;
    const shouldAppend =
      jobProfileAppendRef.current || !!jobProfileFilterData?.data?.previous;
    setJobProfileOptions((prev) =>
      shouldAppend ? mergeUniqueByValue(prev, results) : results,
    );
    setJobProfileNextCursor(getQueryParam(nextUrl, "cursor"));
    setJobProfileHasMore(!!nextUrl);
    jobProfileAppendRef.current = false;
  }, [jobProfileFilterData]);

  useEffect(() => {
    const responseQuery = designationFilterData?.data?.query;
    if (
      responseQuery !== undefined &&
      shouldMatchQuery(responseQuery) &&
      !isSameQuery(responseQuery, designationQueryRef.current)
    ) {
      return;
    }

    const results = designationFilterData?.data?.results || [];
    const nextUrl = designationFilterData?.data?.next;
    const shouldAppend =
      designationAppendRef.current || !!designationFilterData?.data?.previous;
    setDesignationOptions((prev) =>
      shouldAppend ? mergeUniqueByValue(prev, results) : results,
    );
    setDesignationNextCursor(getQueryParam(nextUrl, "cursor"));
    setDesignationHasMore(!!nextUrl);
    designationAppendRef.current = false;
  }, [designationFilterData]);

  useEffect(() => {
    const responseQuery = locationFilterData?.data?.query;
    if (
      responseQuery !== undefined &&
      shouldMatchQuery(responseQuery) &&
      !isSameQuery(responseQuery, locationQueryRef.current)
    ) {
      return;
    }

    const results = locationFilterData?.data?.results || [];
    const nextUrl = locationFilterData?.data?.next;
    const shouldAppend =
      locationAppendRef.current || !!locationFilterData?.data?.previous;
    setLocationOptions((prev) =>
      shouldAppend ? mergeUniqueByValue(prev, results) : results,
    );
    setLocationNextCursor(getQueryParam(nextUrl, "cursor"));
    setLocationHasMore(!!nextUrl);
    locationAppendRef.current = false;
  }, [locationFilterData]);

  const handleCompanyScrollToBottom = () => {
    if (!companyHasMore || companyFilterLoading || !companyNextCursor) return;
    companyAppendRef.current = true;
    dispatch(
      getCompanyFilter({
        field: "company",
        query: companyQueryRef.current,
        cursor: companyNextCursor,
      }),
    );
  };

  const handleJobProfileScrollToBottom = () => {
    if (
      !jobProfileHasMore ||
      jobProfileFilterLoading ||
      !jobProfileNextCursor
    ) {
      return;
    }
    jobProfileAppendRef.current = true;
    dispatch(
      getJobProfileFilter({
        field: "job_profile",
        query: jobProfileQueryRef.current,
        cursor: jobProfileNextCursor,
      }),
    );
  };

  const handleDesignationScrollToBottom = () => {
    if (
      !designationHasMore ||
      designationFilterLoading ||
      !designationNextCursor
    ) {
      return;
    }
    designationAppendRef.current = true;
    dispatch(
      getDesignationFilter({
        field: "designation",
        query: designationQueryRef.current,
        cursor: designationNextCursor,
      }),
    );
  };

  const handleLocationScrollToBottom = () => {
    if (!locationHasMore || locationFilterLoading || !locationNextCursor) {
      return;
    }
    locationAppendRef.current = true;
    dispatch(
      getLocationFilter({
        field: "location",
        query: locationQueryRef.current,
        cursor: locationNextCursor,
      }),
    );
  };

  //user-typed data in fields & show drop down api
  const handleInputChange = (filtername, value) => {
    if (filtername === "company") {
      setCompany(value);
    }
    if (filtername === "jobProfile") {
      setJobProfile(value);
    }
    if (filtername === "designation") {
      setDesignation(value);
    }
    if (filtername === "location") {
      setLocation(value);
    }
    if (filtername === "experience") {
      setExperience(value);
    }
    if (filtername === "postData") {
      setPostDate(value);
    }
    if (filtername === "appEndDate") {
      setApplicationEndDate(value);
    }
    if (filtername === "url") {
      setUrl(value);
    }
    if (filtername === "description") {
      setDescription(value);
    }
  };

  const handleSelectInputChange = (filtername) => (value, actionMeta) => {
    const action = actionMeta?.action;
    if (
      action === "input-change" ||
      action === "set-value" ||
      action === "clear"
    ) {
      handleInputChange(filtername, value);
    }
  };

  //on selecting from drop down:
  const handleFilterChange = (filtername, value) => {
    if (filtername === "company") {
      setSelectedCompany(value);
    }
    if (filtername === "jobProfile") {
      setSelectedJobProfile(value);
    }
    if (filtername === "designation") {
      setSelectedDesignation(value);
    }
    if (filtername === "location") {
      setSelectedLocation(value);
    }
    if (filtername === "jobType") {
      setSelectedJobType(value);
    }
  };

  const handleUpdateJobOpening = async () => {
    let tempError = {
      ...error,
      companyError: selectedCompany ? false : true,
      jobProfileError: selectedJobProfile ? false : true,
      postDateError: isPublished ? (postDate ? false : true) : false,
      appEndDateError: applicationEndDate
        ? new Date(applicationEndDate) < new Date(postDate)
        : false,
      urlError: url.trim()
        ? !/^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i.test(
            url.trim(),
          )
        : isPublished, // Mandatory only if published
      descriptionError: isPublished
        ? description?.trim()
          ? false
          : true
        : false,
      designationError: isPublished
        ? selectedDesignation
          ? false
          : true
        : false,
      locationError: isPublished ? (selectedLocation ? false : true) : false,
      jobTypeError: isPublished ? (selectedJobType ? false : true) : false,
    };
    setError(tempError);

    if (Object.values(tempError).every((value) => value === false)) {
      const payload = {
        experience_required: experience,
        company: selectedCompany?.value ?? null,
        job_profile: selectedJobProfile?.value ?? null,
        designation: selectedDesignation?.value ?? null,
        location: selectedLocation?.value ?? null,
        job_type: selectedJobType?.value ?? null,
        post_date: postDate,
        end_date: applicationEndDate,
        application_link: url,
        description: description,
        status: isPublished ? 1 : 0, // 1 = Active, 0 = Inactive
      };
      setIsUpdating(true);
      try {
        const response = await putJobOpeningRepo(jobId, payload);
        if (response?.data?.result) {
          navigate("/cms/manage-job-opening");
          dispatch(getJobOpenings());
        }
      } catch (e) {
        setErrorAlertOpen(true);
      }
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    navigate("/cms/manage-job-opening");
  };

  return (
    <>
      {/* Toaster */}
      <Snackbar
        open={errorAlertOpen}
        autoHideDuration={3000}
        onClose={() => setErrorAlertOpen(false)}
      >
        <Alert severity="error">Failed to edit the job.</Alert>
      </Snackbar>
      <h4 style={{ marginTop: "40px" }}>
        {" "}
        Edit Job Opening {displayJobProfile && "- "}
        {displayJobProfile && displayJobProfile}
      </h4>
      <div className="mb-5">
        <Link to="/cms/manage-job-opening">Manage Job Openings</Link> &gt;{" "}
        <span className="text-success-emphasis">Edit Opening</span>
      </div>
      {/* form started */}
      <div>
        {/* Mapping Details started */}
        <h5 className="mt-5 mb-3">Mapping Detail</h5>
        <div className="examfilter mb-3">
          <Row>
            <Col md={6}>
              <Form.Group
                className="mb-3"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <Form.Label htmlFor="company">
                  Company<span style={{ color: "red" }}> *</span>
                </Form.Label>
                <InfiniteScrollSelect
                  inputId="company"
                  name="company"
                  placeholder="Select Company"
                  isSearchable
                  isClearable
                  isLoading={companyFilterLoading}
                  options={companyOptions}
                  value={selectedCompany}
                  inputValue={company}
                  onInputChange={handleSelectInputChange("company")}
                  onChange={(obj) => handleFilterChange("company", obj)}
                  onMenuScrollToBottom={handleCompanyScrollToBottom}
                  filterOption={() => true}
                  classNamePrefix="react-select"
                  maxMenuHeight={SELECT_MENU_MAX_HEIGHT}
                />
                <p className="text-secondary mb-0">
                  Can't find the company?{" "}
                  <a
                    href={`https://cms${process.env.REACT_APP_SUB_DOMAIN_NAME}/dj-admin/manage-companies`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Manage Companies
                  </a>
                </p>
                {error.companyError && (
                  <div className="text-danger small mt-1">
                    {mendatoryErrorText}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group
                className="mb-3"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <Form.Label htmlFor="jobProfile">
                  Job Profile <span style={{ color: "red" }}> *</span>
                </Form.Label>
                <InfiniteScrollSelect
                  inputId="jobProfile"
                  placeholder="Select Job Profile"
                  isSearchable
                  isClearable
                  isLoading={jobProfileFilterLoading}
                  options={jobProfileOptions}
                  value={selectedJobProfile}
                  inputValue={jobProfile}
                  onInputChange={handleSelectInputChange("jobProfile")}
                  onChange={(obj) => handleFilterChange("jobProfile", obj)}
                  onMenuScrollToBottom={handleJobProfileScrollToBottom}
                  filterOption={() => true}
                  maxMenuHeight={SELECT_MENU_MAX_HEIGHT}
                />

                <p className="text-secondary mb-0">
                  Can't find the profile?{" "}
                  <a
                    href={`https://cms${process.env.REACT_APP_SUB_DOMAIN_NAME}/dj-admin/manage-jobs`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Manage Job Profiles
                  </a>
                </p>
                {error.jobProfileError && (
                  <div className="text-danger small mt-1">
                    {mendatoryErrorText}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
        </div>
        {/* Mapping Details ended */}
        {/* Job Detail started */}
        <h5 className="mt-5 mb-3">Job Detail</h5>
        <div className="examfilter mb-3">
          <Row>
            <Col md={6}>
              <Form.Group
                className="mb-3"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <Form.Label htmlFor="designation">
                  Designation{isPublished && <span style={{ color: "red" }}> *</span>}
                </Form.Label>
                <InfiniteScrollSelect
                  inpputId="designation"
                  placeholder="e.g. Senior Dietitian"
                  isSearchable
                  isClearable
                  isLoading={designationFilterLoading}
                  options={designationOptions}
                  value={selectedDesignation}
                  inputValue={designation}
                  onInputChange={handleSelectInputChange("designation")}
                  onChange={(obj) => handleFilterChange("designation", obj)}
                  onMenuScrollToBottom={handleDesignationScrollToBottom}
                  filterOption={() => true}
                  maxMenuHeight={SELECT_MENU_MAX_HEIGHT}
                />
                {error.designationError && (
                  <div className="text-danger small mt-1">
                    {mendatoryErrorText}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group
                className="mb-3"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <Form.Label htmlFor="location">
                  Location{isPublished && <span style={{ color: "red" }}> *</span>}
                </Form.Label>
                <InfiniteScrollSelect
                  inpputId="location"
                  placeholder="e.g. Mumbai, Remote"
                  isSearchable
                  isClearable
                  isLoading={locationFilterLoading}
                  options={locationOptions}
                  value={selectedLocation}
                  inputValue={location}
                  onInputChange={handleSelectInputChange("location")}
                  onChange={(obj) => handleFilterChange("location", obj)}
                  onMenuScrollToBottom={handleLocationScrollToBottom}
                  filterOption={() => true}
                  maxMenuHeight={SELECT_MENU_MAX_HEIGHT}
                />
                {error.locationError && (
                  <div className="text-danger small mt-1">
                    {mendatoryErrorText}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="jobType">
                  Job Type{isPublished && <span style={{ color: "red" }}> *</span>}
                </Form.Label>
                <Select
                  inputId="jobType"
                  placeholder="Select Job Type"
                  isSearchable={false}
                  isClearable
                  isLoading={jobTypeFilterLoading}
                  options={jobTypeFilterData?.data?.results || []}
                  value={selectedJobType}
                  onChange={(obj) => handleFilterChange("jobType", obj)}
                  filterOption={() => true}
                />
                {error.jobTypeError && (
                  <div className="text-danger small mt-1">
                    {mendatoryErrorText}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group
                className="mb-3"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <Form.Label htmlFor="exp">Experience Required</Form.Label>
                <Form.Control
                  id="exp"
                  type="text"
                  className={"input_pd"}
                  value={experience}
                  onChange={(e) =>
                    handleInputChange("experience", e.target.value)
                  }
                  placeholder="e.g. 5-8 Years"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="postDate">
                  Post Date{isPublished && <span style={{ color: "red" }}> *</span>}
                </Form.Label>
                <Form.Control
                  id="postDate"
                  type="date"
                  className={"input_pd"}
                  value={postDate}
                  min={MIN_ALLOWED_DATE}
                  onKeyDown={(e) => e.preventDefault()}
                  onChange={(e) =>
                    handleInputChange("postData", e.target.value)
                  }
                  onClick={(e) => e.target.showPicker?.()}
                />
                {error.postDateError && (
                  <div className="text-danger small mt-1">
                    {mendatoryErrorText}
                  </div>
                )}
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group
                className="mb-3"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <Form.Label htmlFor="appEndDate">
                  Application End Date
                </Form.Label>
                <Form.Control
                  id="appEndDate"
                  type="date"
                  className={"input_pd"}
                  value={applicationEndDate}
                  min={
                    postDate && postDate > MIN_ALLOWED_DATE
                      ? postDate
                      : MIN_ALLOWED_DATE
                  }
                  onKeyDown={(e) => e.preventDefault()}
                  onChange={(e) =>
                    handleInputChange("appEndDate", e.target.value)
                  }
                  onClick={(e) => e.target.showPicker?.()}
                />
                {error.appEndDateError && (
                  <div className="text-danger small mt-1">
                    Please fill valid End Date
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Form.Group
                className="mb-3"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <Form.Label>
                  Description{isPublished && <span style={{ color: "red" }}> *</span>}
                </Form.Label>
                <CustomEditor5
                  initData={description}
                  onChange={(data) => {
                    handleInputChange("description", data);
                  }}
                  placeholder="Type the description"
                />
                {error.descriptionError && (
                  <div className="text-danger small mt-1">
                    {mendatoryErrorText}
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group
                className="mb-3"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <Form.Label>
                  Application URL{isPublished && <span style={{ color: "red" }}> *</span>}
                </Form.Label>
                <Form.Control
                  type="text"
                  className={"input_pd"}
                  placeholder="https://company.com/careers/job-id-123"
                  value={url}
                  onChange={(e) => handleInputChange("url", e.target.value)}
                />
                {error.urlError && (
                  <div className="text-danger small mt-1">
                    Please fill valid URL
                  </div>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Form.Group
                className="mb-4"
                style={{ display: "flex", flexDirection: "column" }}
              >
                <Form.Check
                  type="checkbox"
                  id="publishStatus"
                  label="Publish this job opening (Active)"
                  checked={isPublished}
                  onChange={(e) => setIsPublished(e.target.checked)}
                  style={{ marginBottom: "4px" }}
                />
                <Form.Text
                  className="text-muted"
                  style={{ marginLeft: "24px" }}
                >
                  {isPublished
                    ? "This job will be visible to users"
                    : "This job will be saved as inactive"}
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group style={{ marginTop: "8px" }}>
                <div>
                  <button
                    className="btnInfo"
                    type="button"
                    onClick={handleUpdateJobOpening}
                    disabled={isUpdating}
                  >
                    {isUpdating ? "Updating..." : "Update Opening"}
                  </button>
                  <button
                    className="secondaryBtn"
                    type="button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                </div>
              </Form.Group>
            </Col>
          </Row>
        </div>
        {/* Opening Details ended */}
      </div>
    </>
  );
};
